import { isValidHandle, normalizeHandle, flagEmoji } from "@shared/flag";
import { autoSignIn, claim, type Identity } from "./handle";
import { randomHandle } from "./random-handle";
import { biomeForDate, courseNameForDate } from "@shared/biome";
import { courseDateAt } from "@shared/course";
import {
  apiCountryLeaderboard,
  apiLiveLeaderboard,
  apiLobby,
  apiReact,
  apiReactions,
  type ReactionEmoji,
} from "./api";

type StatusKind = "ok" | "err" | "dim";

export type JoinMode = "daily" | "endless" | "runner";

export type MenuHandle = {
  dispose: () => void;
  /** Reset back to the handle step — called after a server-side rejection
   *  (e.g. name already on today's leaderboard) so the user can pick a new
   *  handle without reloading. */
  reset: () => void;
  /** Jump straight to the mode chooser with the current identity. Used
   *  by the in-game "back to menu" button so the user can swap modes
   *  without retyping their name. */
  showModeChooser: (identity: Identity) => void;
  /** Overwrite the handle-step status line (e.g. to surface a
   *  "name taken today" rejection from a parent callback). */
  showHandleStatus: (text: string, kind: "ok" | "err" | "dim") => void;
};

export interface MountMenuOptions {
  onJoined: (identity: Identity, mode: JoinMode) => void;
  /** Click handler for the "▶ Watch round" CTA on the Yesterday's #1
   *  card. Fired with the prior day's winner handle + courseDate so
   *  main.ts can drive the spectator-mode lifecycle. */
  onWatchYesterday?: (handle: string, courseDate: string) => void;
}

export function mountMenu(
  root: HTMLElement,
  options: MountMenuOptions,
): MenuHandle {
  const { onJoined, onWatchYesterday } = options;
  const handleSection = root.querySelector<HTMLElement>("#handle-section")!;
  const modeSection = root.querySelector<HTMLElement>("#mode-section")!;
  const input = root.querySelector<HTMLInputElement>("#handle-input")!;
  const status = root.querySelector<HTMLDivElement>("#handle-status")!;
  const claimBtn = root.querySelector<HTMLButtonElement>("#claim-btn")!;
  const shuffleBtn = root.querySelector<HTMLButtonElement>("#shuffle-btn");
  const competeBtn = root.querySelector<HTMLButtonElement>("#mode-compete-btn")!;
  const endlessBtn = root.querySelector<HTMLButtonElement>("#mode-endless-btn");
  const runnerBtn = root.querySelector<HTMLButtonElement>("#mode-runner-btn");
  const modeBackBtn = root.querySelector<HTMLButtonElement>("#mode-back-btn")!;
  const modeHandleDisplay = root.querySelector<HTMLElement>(
    "#mode-handle-display",
  )!;

  let currentIdentity: Identity | null = null;

  const setStatus = (text: string, kind: StatusKind = "dim") => {
    status.textContent = text;
    status.className = `status ${kind}`;
  };

  // Format-only validation; the server no longer reserves handles so
  // there's no availability call to wait on.
  const validateFormat = (raw: string): string | null => {
    const h = normalizeHandle(raw);
    if (!h) return null;
    if (!isValidHandle(h)) return null;
    return h;
  };

  const onInput = () => {
    input.value = input.value.replace(/[^a-zA-Z0-9_]/g, "");
    const h = validateFormat(input.value);
    if (!h) {
      setStatus("Letters, numbers, and _ only (2–16 chars).", "dim");
      claimBtn.disabled = true;
    } else {
      setStatus(`Ready to play as "${h}".`, "ok");
      claimBtn.disabled = false;
    }
  };

  const enterModeChooser = (identity: Identity) => {
    currentIdentity = identity;
    modeHandleDisplay.textContent = `${identity.handle} ${identity.flag}`;
    handleSection.hidden = true;
    modeSection.hidden = false;
  };

  const returnToHandleStep = () => {
    modeSection.hidden = true;
    handleSection.hidden = false;
    currentIdentity = null;
  };

  const attemptClaim = async () => {
    const h = validateFormat(input.value);
    if (!h) return;
    claimBtn.disabled = true;
    setStatus("Saving…", "dim");
    const outcome = await claim(h);
    if (outcome.kind === "joined") {
      enterModeChooser(outcome.identity);
      return;
    }
    if (outcome.kind === "invalid") {
      setStatus("Invalid handle", "err");
    } else {
      setStatus("Couldn't save — try again", "err");
    }
    claimBtn.disabled = false;
  };

  const onClaimClick = () => void attemptClaim();

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !claimBtn.disabled) void attemptClaim();
  };

  input.addEventListener("input", onInput);
  input.addEventListener("keydown", onKey);
  claimBtn.addEventListener("click", onClaimClick);

  competeBtn.addEventListener("click", () => {
    if (!currentIdentity) return;
    onJoined(currentIdentity, "daily");
  });
  endlessBtn?.addEventListener("click", () => {
    if (!currentIdentity) return;
    onJoined(currentIdentity, "endless");
  });
  runnerBtn?.addEventListener("click", () => {
    if (!currentIdentity) return;
    onJoined(currentIdentity, "runner");
  });
  modeBackBtn.addEventListener("click", returnToHandleStep);

  // Belt-and-suspenders: on iOS Safari, call focus() synchronously inside
  // the user's touch gesture so the keyboard trust check passes.
  input.addEventListener("touchend", () => input.focus());

  const reroll = () => {
    input.value = randomHandle();
    onInput();
  };
  shuffleBtn?.addEventListener("click", reroll);

  // Today's course card — name + tagline derived from daily seed.
  const todayName = root.querySelector<HTMLElement>("#today-course-name");
  const todaySub = root.querySelector<HTMLElement>("#today-course-sub");
  const todayDate = courseDateAt(Date.now());
  const theme = biomeForDate(todayDate);
  if (todayName) todayName.textContent = courseNameForDate(todayDate);
  if (todaySub) todaySub.textContent = `${theme.name} · ${theme.tagline}`;

  const countdownEl = root.querySelector<HTMLElement>("#lb-countdown");
  const tickCountdown = () => {
    if (!countdownEl) return;
    const now = Date.now();
    const rolloverMs = 13 * 3600_000 + 37 * 60_000;
    const nowUtcStart = Math.floor(now / 86400_000) * 86400_000;
    let next = nowUtcStart + rolloverMs;
    if (next <= now) next += 86400_000;
    const ms = Math.max(0, next - now);
    const h = Math.floor(ms / 3600_000);
    const m = Math.floor((ms % 3600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    countdownEl.textContent = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };
  tickCountdown();
  const countdownTimer = window.setInterval(tickCountdown, 1000);

  // Leaderboard preview — three tabs: Top (finished scores), Live
  // (in-progress rounds), Country (avg strokes per country). Each tab
  // fetches on first activation and again every 8-20s while active.
  const lbBody = root.querySelector<HTMLElement>("#welcome-lb-body");

  // ---------- Reactions on Yesterday's #1 card ----------
  // Single-target reactions: only the previous day's winner gets a
  // celebratory 👏 / 🔥 / 💀 strip on their card. Keeps the Top tab
  // rows compact and concentrates "people are reacting!" energy on
  // one prominent figure.
  const reactionEmoji: Record<ReactionEmoji, string> = {
    clap: "👏",
    fire: "🔥",
    skull: "💀",
  };
  const reactionEmojiList: ReactionEmoji[] = ["clap", "fire", "skull"];
  const yesterdayReactions = document.getElementById(
    "yesterday-reactions",
  ) as HTMLElement | null;
  // Live state for the yesterday card. courseDate is set from the
  // /api/lobby response so reactions hit the right day's bucket.
  // `eventsSince` advances each refresh so we only float NEW events
  // (i.e. taps from other players) rather than the full ~30s backlog.
  const yState: {
    courseDate: string | null;
    handle: string | null;
    counts: Map<ReactionEmoji, number>;
    tapped: Set<ReactionEmoji>;
    eventsSince: number;
  } = {
    courseDate: null,
    handle: null,
    counts: new Map(),
    tapped: new Set(),
    eventsSince: 0,
  };

  // Spawn a single floating emoji from a screen position. Used for
  // both the local user's tap (instant feedback) and remote players'
  // taps (picked up on the next reactions poll).
  const spawnReactionFloat = (emoji: ReactionEmoji, x: number, y: number): void => {
    const el = document.createElement("div");
    el.className = "reaction-float";
    el.textContent = reactionEmoji[emoji];
    // Random horizontal drift ±60px, rotation ±35deg, peak scale
    // 1.2-1.55 — varies just enough that a salvo doesn't read as a
    // single thick column.
    const drift = (Math.random() - 0.5) * 120;
    const rot = (Math.random() - 0.5) * 70;
    const peakScale = 1.2 + Math.random() * 0.35;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.setProperty("--rf-x", `${drift}px`);
    el.style.setProperty("--rf-rot", `${rot}deg`);
    el.style.setProperty("--rf-scale", String(peakScale));
    document.body.appendChild(el);
    // 1.8s animation + a 200ms grace before removal so the final
    // frame has time to render even on slow devices.
    window.setTimeout(() => el.remove(), 2000);
  };

  // Pick a sensible spawn point for a remote-event float (the local
  // user has button rect, remotes don't). Use the strip's center as
  // the origin so floats appear to rise from the buttons.
  const spawnFloatFromStrip = (emoji: ReactionEmoji): void => {
    if (!yesterdayReactions) return;
    const r = yesterdayReactions.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return; // strip not visible
    // Aim from a random reaction button if we can find one matching
    // the emoji; else fall back to the strip's center.
    const btn = yesterdayReactions.querySelector<HTMLElement>(
      `.reaction-btn[data-emoji="${emoji}"]`,
    );
    const target = (btn ?? yesterdayReactions).getBoundingClientRect();
    const x = target.left + target.width / 2 - 13; // centre on the 26px float
    const y = target.top - 4;
    spawnReactionFloat(emoji, x, y);
  };

  const renderYesterdayReactions = (): void => {
    if (!yesterdayReactions) return;
    if (!yState.handle || !yState.courseDate) {
      yesterdayReactions.innerHTML = "";
      return;
    }
    yesterdayReactions.dataset.reactHandle = yState.handle;
    yesterdayReactions.dataset.reactDate = yState.courseDate;
    yesterdayReactions.innerHTML = reactionEmojiList
      .map((e) => {
        const count = yState.counts.get(e) ?? 0;
        const isTapped = yState.tapped.has(e);
        return `
          <button type="button" class="reaction-btn${
            isTapped ? " tapped" : ""
          } visible" data-emoji="${e}" aria-label="React with ${e}">
            <span class="reaction-emoji">${reactionEmoji[e]}</span><span class="reaction-count"${
              count > 0 ? "" : ' hidden'
            }>${count}</span>
          </button>
        `;
      })
      .join("");
  };

  // Track which reaction-event ts'es we've already floated so a single
  // tap doesn't spawn duplicate floats (local optimistic + server poll
  // both reference the same event).
  const seenReactionEventTs = new Set<number>();
  // Recent local taps recorded by emoji + approx client-side ts. Used
  // to suppress server-echoed events that came from our own click —
  // otherwise the user sees a float on tap AND another float ~12s
  // later when the poll returns. A 4s window is more than enough to
  // cover server round-trip + next-poll delay.
  const localTapHistory: Array<{ emoji: ReactionEmoji; ts: number }> = [];
  const LOCAL_TAP_WINDOW_MS = 4000;
  const isLikelyOwnTap = (emoji: ReactionEmoji, eventTs: number): boolean => {
    for (const t of localTapHistory) {
      if (t.emoji !== emoji) continue;
      if (Math.abs(t.ts - eventTs) < LOCAL_TAP_WINDOW_MS) return true;
    }
    return false;
  };
  // Cap: don't float more than this many in a single poll cycle so a
  // burst doesn't carpet the screen.
  const MAX_FLOATS_PER_POLL = 12;

  const refreshYesterdayReactions = async (): Promise<void> => {
    if (!yState.handle || !yState.courseDate) return;
    try {
      const res = await apiReactions(yState.courseDate, yState.eventsSince);
      yState.eventsSince = res.serverTime;
      // Filter to just the winner's row and rebuild the count map.
      const fresh = new Map<ReactionEmoji, number>();
      for (const r of res.entries) {
        if (r.handle !== yState.handle) continue;
        fresh.set(r.emoji, r.count);
      }
      yState.counts = fresh;
      renderYesterdayReactions();

      // Spawn floats for events we haven't seen yet — these include
      // both other players' taps and our own (the server echoes them
      // back). Local-tap floats are deduped by `seenReactionEventTs`
      // because the optimistic spawn marks the timestamp pre-emptively.
      let spawned = 0;
      for (const ev of res.events) {
        if (ev.handle !== yState.handle) continue;
        if (seenReactionEventTs.has(ev.ts)) continue;
        seenReactionEventTs.add(ev.ts);
        // Server echoes our own taps too — skip those since we
        // already spawned an instant float on click.
        if (isLikelyOwnTap(ev.emoji, ev.ts)) continue;
        if (spawned >= MAX_FLOATS_PER_POLL) continue;
        spawned++;
        // Stagger so a burst rises in waves rather than a single
        // wall — each event gets up to 250ms of jitter on top of an
        // index-based delay.
        const delay = spawned * 80 + Math.random() * 250;
        window.setTimeout(() => spawnFloatFromStrip(ev.emoji), delay);
      }
      // Trim the seen set so it doesn't grow unbounded across polls.
      if (seenReactionEventTs.size > 200) {
        const arr = Array.from(seenReactionEventTs).sort((a, b) => a - b);
        for (let i = 0; i < arr.length - 100; i++) {
          seenReactionEventTs.delete(arr[i]!);
        }
      }
    } catch {
      /* poll is best-effort */
    }
  };

  const onYesterdayReactClick = async (btn: HTMLElement): Promise<void> => {
    const emoji = btn.dataset.emoji as ReactionEmoji | undefined;
    if (
      !emoji ||
      !reactionEmojiList.includes(emoji) ||
      !yState.handle ||
      !yState.courseDate
    ) {
      return;
    }
    yState.counts.set(emoji, (yState.counts.get(emoji) ?? 0) + 1);
    yState.tapped.add(emoji);
    btn.classList.add("tapped", "burst");
    window.setTimeout(() => btn.classList.remove("burst"), 320);
    // Record this tap so the next poll-cycle's matching server event
    // doesn't spawn a duplicate float. Trim history to the last 30s.
    const now = Date.now();
    localTapHistory.push({ emoji, ts: now });
    while (
      localTapHistory.length > 0 &&
      now - localTapHistory[0]!.ts > 30_000
    ) {
      localTapHistory.shift();
    }
    // Spawn the float immediately for instant local feedback —
    // origin is the button itself, so the emoji rises right out of
    // where the user just tapped.
    const r = btn.getBoundingClientRect();
    spawnReactionFloat(emoji, r.left + r.width / 2 - 13, r.top - 4);
    // Two more flying companions for satisfying double/triple-tap
    // rhythm — staggered by a few ms so they don't merge.
    window.setTimeout(
      () =>
        spawnReactionFloat(
          emoji,
          r.left + r.width / 2 - 13 + (Math.random() - 0.5) * 16,
          r.top - 4,
        ),
      90,
    );
    renderYesterdayReactions();
    const result = await apiReact(yState.courseDate, yState.handle, emoji);
    if (result.ok && typeof result.count === "number") {
      yState.counts.set(emoji, result.count);
      renderYesterdayReactions();
    }
  };

  if (yesterdayReactions) {
    yesterdayReactions.addEventListener("click", (e) => {
      const t = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        ".reaction-btn",
      );
      if (t) void onYesterdayReactClick(t);
    });
  }
  type TopEntry = {
    handle: string;
    countryCode?: string | null;
    totalStrokes: number;
  };
  const renderTop = (entries: TopEntry[]) => {
    if (!lbBody) return;
    if (entries.length === 0) {
      lbBody.innerHTML =
        '<div class="welcome-lb-empty">Be the first to finish today. Your name appears here.</div>';
      return;
    }
    const html = entries
      .slice(0, 6)
      .map((e, i) => {
        const rank = i + 1;
        const rankClass = rank <= 3 ? `rank r${rank}` : "rank";
        const rankGlyph = rank === 1 ? "👑" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : String(rank);
        return `
          <div class="welcome-lb-row">
            <div class="${rankClass}">${rankGlyph}</div>
            <div class="player">
              <span class="flag">${flagEmoji(e.countryCode)}</span>
              <span class="handle">${escapeHtml(e.handle)}</span>
            </div>
            <div class="score">${e.totalStrokes}</div>
            <div class="holes">9/9</div>
          </div>
        `;
      })
      .join("");
    lbBody.innerHTML = html;
  };

  const renderCountry = (
    entries: Array<{
      countryCode: string;
      rounds: number;
      avgStrokes: number;
      bestStrokes: number;
    }>,
  ) => {
    if (!lbBody) return;
    if (entries.length === 0) {
      lbBody.innerHTML =
        '<div class="welcome-lb-empty">No country scores yet today — be first to put your flag on the board.</div>';
      return;
    }
    const html = entries
      .slice(0, 6)
      .map((e, i) => {
        const rank = i + 1;
        const rankClass = rank <= 3 ? `rank r${rank}` : "rank";
        const rankGlyph = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : String(rank);
        return `
          <div class="welcome-lb-row">
            <div class="${rankClass}">${rankGlyph}</div>
            <div class="player">
              <span class="flag">${flagEmoji(e.countryCode)}</span>
              <span class="handle">${e.countryCode}</span>
            </div>
            <div class="score">${e.avgStrokes.toFixed(1)}</div>
            <div class="holes dim">${e.rounds} ${e.rounds === 1 ? "round" : "rounds"}</div>
          </div>
        `;
      })
      .join("");
    lbBody.innerHTML = html;
  };

  const renderLive = (
    entries: Array<{
      handle: string;
      countryCode: string;
      currentHole: number;
      holesCompleted: number;
      totalStrokes: number;
    }>,
  ) => {
    if (!lbBody) return;
    if (entries.length === 0) {
      lbBody.innerHTML =
        '<div class="welcome-lb-empty">No rounds in progress right now.</div>';
      return;
    }
    const html = entries
      .slice(0, 6)
      .map((e, i) => {
        const rank = i + 1;
        const rankClass = rank <= 3 ? `rank r${rank}` : "rank";
        const rankGlyph = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : String(rank);
        // Show "thru H_N+1" so a player on currentHole=4 reads "thru H4"
        // (4 holes complete) — matches how golf scoring usually phrases it.
        const thru = e.holesCompleted > 0 ? `thru H${e.holesCompleted}` : `on H${e.currentHole + 1}`;
        return `
          <div class="welcome-lb-row">
            <div class="${rankClass}">${rankGlyph}</div>
            <div class="player">
              <span class="flag">${flagEmoji(e.countryCode)}</span>
              <span class="handle">${escapeHtml(e.handle)}</span>
            </div>
            <div class="score">${e.totalStrokes}</div>
            <div class="holes dim">${thru}</div>
          </div>
        `;
      })
      .join("");
    lbBody.innerHTML = html;
  };

  type LbTab = "top" | "live" | "country";
  let activeTab: LbTab = "top";
  let lbRefreshTimer: number | null = null;

  const fetchAndRender = async (tab: LbTab) => {
    if (!lbBody) return;
    if (tab === "top") {
      try {
        const r = await fetch("/api/leaderboard/today");
        const data = (await r.json()) as { entries: TopEntry[] };
        if (activeTab === "top") renderTop(data.entries ?? []);
      } catch {
        if (activeTab === "top") renderTop([]);
      }
    } else if (tab === "live") {
      try {
        const data = await apiLiveLeaderboard();
        if (activeTab === "live") renderLive(data.entries ?? []);
      } catch {
        if (activeTab === "live") renderLive([]);
      }
    } else {
      try {
        const data = await apiCountryLeaderboard();
        if (activeTab === "country") renderCountry(data.entries ?? []);
      } catch {
        if (activeTab === "country") renderCountry([]);
      }
    }
  };

  const setTab = (tab: LbTab) => {
    activeTab = tab;
    void fetchAndRender(tab);
    if (lbRefreshTimer !== null) window.clearInterval(lbRefreshTimer);
    // Live rolls fastest (in-progress data); Top + Country are slower-
    // moving so a 20s tick is fine. Reactions piggy-back on the Top
    // poll cycle so the strip counts stay fresh.
    if (tab === "live") {
      lbRefreshTimer = window.setInterval(() => void fetchAndRender("live"), 8000);
    } else if (tab === "top") {
      lbRefreshTimer = window.setInterval(() => void fetchAndRender("top"), 20000);
    } else {
      lbRefreshTimer = window.setInterval(() => void fetchAndRender("country"), 20000);
    }
  };

  const tabButtons = root.querySelectorAll<HTMLButtonElement>(".lb-tab");
  tabButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      tabButtons.forEach((b) => b.classList.toggle("active", b === btn));
      const tab = (btn.dataset.tab ?? "top") as LbTab;
      setTab(tab);
    });
  });
  // Kick off the default Top tab.
  setTab("top");

  // Skip the auto sign-in / random prefill when the player is arriving
  // from the Vibeverse portal — main.ts will drive entry from the URL
  // params and we don't want a racing autoSignIn flicker behind the
  // auto-launch.
  const params = new URLSearchParams(window.location.search);
  const fromPortal =
    params.get("portal") === "true" || params.get("portal") === "1";

  if (!fromPortal) {
    // Try auto sign-in from stored creds — goes straight to mode chooser.
    void (async () => {
      const identity = await autoSignIn();
      if (identity) {
        enterModeChooser(identity);
        return;
      }
      // Pre-fill a random handle on mobile where focus is hard to trigger.
      input.value = randomHandle();
      onInput();
      if (!matchMedia("(pointer: coarse)").matches) input.focus();
    })();
  }

  // ---------- Lobby chrome: presence pill + just-joined feed + winner ----------
  // One bundled poll covers all three. The first response also seeds the
  // "since" timestamp so the next poll only returns NEW joins, not the
  // 5-min backlog.
  const presencePill = document.getElementById(
    "lobby-presence-pill",
  ) as HTMLElement | null;
  const presenceCount = document.getElementById("lobby-presence-count");
  const yesterdayCard = document.getElementById(
    "yesterday-card",
  ) as HTMLElement | null;
  const yesterdayHandle = document.getElementById("yesterday-handle");
  const yesterdayFlag = document.getElementById("yesterday-flag");
  const yesterdaySub = document.getElementById("yesterday-sub");
  const lobbyFeed = document.getElementById(
    "lobby-feed",
  ) as HTMLElement | null;
  // "▶ Watch round" CTA — wired once at mount, reads handle + date
  // from data-attrs the lobby-poll handler stamps on as winner data
  // arrives.
  const yesterdayWatchBtn = document.getElementById(
    "yesterday-watch-btn",
  ) as HTMLButtonElement | null;
  if (yesterdayWatchBtn && onWatchYesterday) {
    yesterdayWatchBtn.addEventListener("click", () => {
      const h = yesterdayWatchBtn.dataset.handle;
      const d = yesterdayWatchBtn.dataset.date;
      if (h && d) onWatchYesterday(h, d);
    });
  }

  // Active count threshold — below this, the pill stays hidden (a "1
  // playing" pill on a quiet day reads more sad than alive). 10+ feels
  // like genuine social proof.
  const PRESENCE_THRESHOLD = 10;
  // Cap how many join pills can stack at once. Older ones fade out.
  const MAX_FEED_PILLS = 4;
  // Track which join events we've already shown (handle:joinedAt) so a
  // poll cycle returning the same record twice doesn't double-render.
  const seenJoins = new Set<string>();
  // First poll asks for everything in the buffer; subsequent polls only
  // ask for joins newer than the last serverTime we received.
  let lastSince = 0;
  let firstLobbyPoll = true;

  const pollLobby = async () => {
    try {
      const res = await apiLobby(lastSince);
      lastSince = res.serverTime;

      if (presencePill && presenceCount) {
        if (res.active >= PRESENCE_THRESHOLD) {
          presenceCount.textContent = String(res.active);
          presencePill.hidden = false;
        } else {
          presencePill.hidden = true;
        }
      }

      if (
        res.yesterday &&
        yesterdayCard &&
        yesterdayHandle &&
        yesterdayFlag &&
        yesterdaySub
      ) {
        yesterdayHandle.textContent = res.yesterday.handle;
        yesterdayFlag.textContent = flagEmoji(res.yesterday.countryCode);
        yesterdaySub.textContent = `${res.yesterday.totalStrokes} strokes · ${res.yesterday.courseDate}`;
        yesterdayCard.hidden = false;
        // Reveal the "▶ Watch round" CTA only when the parent has wired
        // a handler — otherwise the button does nothing visible.
        const watchBtn = document.getElementById(
          "yesterday-watch-btn",
        ) as HTMLButtonElement | null;
        if (watchBtn && onWatchYesterday) {
          watchBtn.hidden = false;
          watchBtn.dataset.handle = res.yesterday.handle;
          watchBtn.dataset.date = res.yesterday.courseDate;
        }
        // First time we see a winner this session, wire up the
        // reactions card. Subsequent polls don't reset the local
        // tapped/state Map so the user's optimistic taps stay sticky
        // even if the lobby endpoint blips.
        const sameWinner =
          yState.handle === res.yesterday.handle &&
          yState.courseDate === res.yesterday.courseDate;
        if (!sameWinner) {
          yState.handle = res.yesterday.handle;
          yState.courseDate = res.yesterday.courseDate;
          yState.counts = new Map();
          yState.tapped = new Set();
          renderYesterdayReactions();
        }
        // Always refresh counts so they tick alongside the lobby poll.
        void refreshYesterdayReactions();
      }

      // On the very first poll we'd otherwise carpet the screen with
      // every join from the last 5 minutes. Mark them all as "seen" but
      // don't render — let the feed warm up with truly new arrivals.
      if (firstLobbyPoll) {
        for (const j of res.joins) seenJoins.add(`${j.handle}:${j.joinedAt}`);
        firstLobbyPoll = false;
        return;
      }

      // Render newest-first (server already sorts) but stagger them so
      // multiple arrivals in one poll don't all whoosh in simultaneously.
      let stagger = 0;
      for (const j of res.joins) {
        const key = `${j.handle}:${j.joinedAt}`;
        if (seenJoins.has(key)) continue;
        seenJoins.add(key);
        const delay = stagger++ * 220;
        window.setTimeout(() => spawnJoinPill(j.handle, j.countryCode), delay);
      }
    } catch {
      /* poll is best-effort; the lobby chrome can stay stale */
    }
  };

  const spawnJoinPill = (handle: string, countryCode: string) => {
    if (!lobbyFeed) return;
    const pill = document.createElement("div");
    pill.className = "lobby-join-pill";
    pill.innerHTML = `
      <span class="ico">${flagEmoji(countryCode)}</span>
      <span><span class="lobby-join-handle">${escapeHtml(handle)}</span> just joined</span>
    `;
    lobbyFeed.appendChild(pill);
    // Cap the stack — fade older pills if we're over budget.
    while (lobbyFeed.children.length > MAX_FEED_PILLS) {
      const oldest = lobbyFeed.firstElementChild as HTMLElement | null;
      if (!oldest) break;
      oldest.classList.add("fading");
      window.setTimeout(() => oldest.remove(), 400);
    }
    // Auto-fade after 12s so the feed self-clears on quiet stretches
    // but still gives casual readers enough time to absorb each pill.
    window.setTimeout(() => {
      pill.classList.add("fading");
      window.setTimeout(() => pill.remove(), 400);
    }, 12000);
  };

  void pollLobby();
  const lobbyTimer = window.setInterval(() => void pollLobby(), 12_000);

  return {
    dispose: () => {
      input.removeEventListener("input", onInput);
      input.removeEventListener("keydown", onKey);
      claimBtn.removeEventListener("click", onClaimClick);
      window.clearInterval(countdownTimer);
      window.clearInterval(lobbyTimer);
      if (lbRefreshTimer !== null) window.clearInterval(lbRefreshTimer);
    },
    reset: () => {
      returnToHandleStep();
    },
    showModeChooser: (identity: Identity) => {
      enterModeChooser(identity);
    },
    showHandleStatus: (text, kind) => {
      setStatus(text, kind);
    },
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&"
      ? "&amp;"
      : c === "<"
        ? "&lt;"
        : c === ">"
          ? "&gt;"
          : c === '"'
            ? "&quot;"
            : "&#39;",
  );
}
